import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { notify } from "@/lib/toast";

/**
 * Builds a stable dedupeKey for the global error toast.
 * A static prefix + error.name + error.status — never user-controlled strings
 * (error.message can embed URLs or repo names).
 */
function makeQueryErrorKey(prefix: string, error: unknown): string {
  const name = error instanceof Error ? error.name : "Error";
  const status = (error as { status?: number } | null)?.status;
  return status != null ? `${prefix}:${name}:${status}` : `${prefix}:${name}`;
}

/**
 * Global react-query error handler — fires a toast for any unhandled query /
 * mutation error so the user gets feedback even if the component doesn't
 * surface an error state.
 * 401 errors are handled upstream (Octokit hook in client.ts) — skipped here
 * to avoid a double toast.
 */
function handleQueryError(error: unknown, cacheKey: string): void {
  const status = (error as { status?: number } | null)?.status;
  if (status === 401) return;
  const message =
    error instanceof Error ? error.message : "An unexpected error occurred";
  notify.error(message, { dedupeKey: makeQueryErrorKey(cacheKey, error) });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => handleQueryError(error, "query-error"),
  }),
  mutationCache: new MutationCache({
    onError: (error) => handleQueryError(error, "mutation-error"),
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  },
});
